import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X, ShieldCheck, GraduationCap, ChevronRight } from "lucide-react";

const STORAGE_KEY = "acdt_emo_popup_dismissed_at";
const COOLDOWN_HOURS = 24; // riapparire dopo 24h dall'ultima chiusura

/**
 * Popup floating "emotion" che invoglia visitatori non loggati a tesserarsi.
 * Si attiva dopo 12 secondi sulla home, slide-in da bottom-right.
 * Dismissible con cooldown di 24h via localStorage.
 */
export default function EmotionPopup() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Skip se già dismessso recentemente
    try {
      const ts = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
      if (ts && Date.now() - ts < COOLDOWN_HOURS * 3600 * 1000) return;
    } catch { /* localStorage non disponibile, mostriamo comunque */ }

    const t = setTimeout(() => setShow(true), 12000);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    setShow(false);
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch {}
  };

  if (!show) return null;

  return (
    <div
      data-testid="emotion-popup"
      className="fixed bottom-5 right-5 z-[80] max-w-[360px] animate-[slideUp_0.5s_ease-out]"
      style={{
        animation: "slideUp 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)",
      }}
    >
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(120%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes pulseGlow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.5); }
          50% { box-shadow: 0 0 0 8px rgba(245, 158, 11, 0); }
        }
        .pulse-cta { animation: pulseGlow 2s infinite; }
      `}</style>

      <div className="rounded-2xl bg-white shadow-2xl border border-[#f59e0b]/30 overflow-hidden relative">
        {/* Header con gradient */}
        <div className="bg-gradient-to-br from-[#0a1f55] via-[#102a6b] to-[#1e3a8a] p-4 pr-10 text-white relative">
          <button
            onClick={dismiss}
            data-testid="emo-popup-close"
            aria-label="Chiudi"
            className="absolute top-2 right-2 h-7 w-7 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl bg-[#f59e0b]/25 flex items-center justify-center flex-shrink-0">
              <Sparkles className="h-5 w-5 text-[#fbbf24]" />
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.2em] font-extrabold text-[#fbbf24]">
                Pronto a iniziare?
              </div>
              <h3 className="font-extrabold text-[16px] leading-tight mt-0.5">
                Diventa Consulente Digitale certificato
              </h3>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-4">
          <p className="text-[13px] text-slate-700 leading-snug">
            Un percorso riconosciuto <strong className="text-[#0a1f55]">L. 4/2013</strong>, attestato A.C.D.T.,
            CFP/CFU e iscrizione al Registro Nazionale.
          </p>

          <ul className="mt-3 space-y-1.5 text-[12.5px]">
            <li className="flex items-center gap-2 text-slate-700">
              <GraduationCap className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />
              <span>Corso 20 ore <span className="line-through text-slate-400">€ 179</span> <strong className="text-red-600">€ 99</strong> <span className="text-[10.5px] font-bold uppercase tracking-wider bg-red-600 text-white px-1.5 py-0.5 rounded ml-0.5">-80€</span></span>
            </li>
            <li className="flex items-center gap-2 text-slate-700">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />
              <span>Tessera + Registro Agg. MIMIT a € 218,90</span>
            </li>
          </ul>

          <div className="mt-4 grid grid-cols-2 gap-2">
            <a
              href="https://assoacdt.com/corso#acquista"
              target="_blank"
              rel="noreferrer"
              data-testid="emo-popup-corso"
              className="pulse-cta inline-flex items-center justify-center gap-1 h-10 px-3 rounded-lg bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1020] font-extrabold text-[12px] transition-colors"
            >
              Acquista corso <ChevronRight className="h-3.5 w-3.5" />
            </a>
            <Link
              to="/area-soci"
              data-testid="emo-popup-soci"
              onClick={() => dismiss()}
              className="inline-flex items-center justify-center gap-1 h-10 px-3 rounded-lg border-2 border-[#102a6b]/20 hover:border-[#102a6b]/50 text-[#102a6b] font-extrabold text-[12px] transition-colors"
            >
              Area Soci
            </Link>
          </div>

          <button
            onClick={dismiss}
            data-testid="emo-popup-dismiss"
            className="mt-3 w-full text-center text-[11px] text-slate-400 hover:text-slate-600"
          >
            Non ora
          </button>
        </div>
      </div>
    </div>
  );
}
